import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * The user settings pages read as one continuous form on a white background:
 * no boxed cards, just small uppercase section labels separated by hairline
 * dividers. Wrap a page's groups in {@link SettingsSections} and introduce each
 * group with a {@link SettingsSection}, so new sections (2FA, sessions, project
 * invitations, …) can be appended without restructuring the page.
 */
export const SettingsSections = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function SettingsSections({ className, children, ...props }, ref) {
  return (
    <div ref={ref} className={cn("divide-y divide-border", className)} {...props}>
      {children}
    </div>
  );
});

export interface SettingsSectionProps extends React.HTMLAttributes<HTMLElement> {
  label: string;
  description?: React.ReactNode;
}

/**
 * The uppercase section-label typography, exported so sections with a custom
 * layout (e.g. {@link ProfileAvatarSection}) render the exact same label style
 * instead of re-declaring it — mirroring `pageSubtitleClassName`.
 */
export const settingsSectionLabelClassName =
  "text-xs font-semibold uppercase tracking-wider text-muted-foreground";

export const SettingsSection = React.forwardRef<HTMLElement, SettingsSectionProps>(
  function SettingsSection({ label, description, className, children, ...props }, ref) {
    return (
      <section ref={ref} className={cn("space-y-4 py-6 first:pt-0", className)} {...props}>
        <div className="space-y-1">
          <h2 className={settingsSectionLabelClassName}>{label}</h2>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {children}
      </section>
    );
  }
);
